<template>
    <div>
        <Header title="Notification push" />

        <div class="ppv2-mt-5">
            <DataTable
                :key="datatableKey"
                :columns="userColumns"
                :items="items"
                :current-page="currentPage"
                :total-pages="totalPages"
                :enable-multi-select="false"
                :enable-drag-drop="false"
                @page-changed="handlePageChange"
                @sort="onSort"
            >
                <template #toolbar>
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                        <div class="ppv2-flex-1">
                            <!-- Search Input -->
                            <div class="ppv2-relative ppv2-gap-3 ppv2-flex">
                                <Search v-model="searchQuery" :emitGlobal="false" @search-updated="onSearch" />
                                <ul class="nav nav-tabs" role="tablist">
                                    <li class="nav-item mx-1">
                                        <button
                                            class="nav-link"
                                            :class="{ active: activeTab === 'current' }"
                                            id="btabs-notif-table-tab"
                                            type="button"
                                            @click="changeTab('current')"
                                        >
                                            En cours
                                        </button>
                                    </li>
                                    <li class="nav-item mx-1">
                                        <button
                                            class="nav-link"
                                            :class="{ active: activeTab === 'history' }"
                                            id="btabs-notif-history-tab"
                                            type="button"
                                            @click="changeTab('history')"
                                        >
                                            Historique
                                        </button>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <button
                            class="ppv2-min-h-[40px] ppv2-flex ppv2-items-center !ppv2-text-white hover:ppv2-text-white ppv2-gap-3 ppv2-pl-4 ppv2-pr-[20px] ppv2-py-2 ppv2-rounded-xl ppv2-bg-[#7D2DBD] ppv2-text-white hover:ppv2-bg-[#6b26a3] ppv2-transition-all ppv2-shadow-lg poppinsRegular"
                            @click="handlePage"
                            type="button"
                        >
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                                Ajouter une notification
                            </span>
                        </button>
                    </div>
                </template>

                <template #column-title_notif="{ item }">
                    <div v-html="item.title_notif"></div>
                </template>

                <template #column-desc_notif="{ item }">
                    <div v-html="item.desc_notif"></div>
                </template>

                <template #column-updated_at="{ item }">
                    <div>{{ formatDate(item.updated_at) }}</div>
                </template>

                <template #column-actions="{ item }">
                    <div
                        class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                        :style="{ fontSize: normalizeFontSize(18) }"
                    >
                        <a
                            :href="`/back-office/communications/notifications/edit/${item.id}`"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_model_template_edit.actions.modify.title')"
                        >
                            <SquarePen :size="18" />
                        </a>
                        <a
                            :href="`/back-office/communications/notifications/destroy/${item.id}`"
                            class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_model_template_edit.actions.delete.title')"
                        >
                            <Trash2 :size="18" />
                        </a>
                    </div>
                </template>
            </DataTable>
        </div>
    </div>
</template>

<style scoped>
.btn:hover {
    color: white; /* text color on hover */
}
.container {
    width: 100%;
}
</style>

<script>
import Header from '../../Shared/Header.vue'
import DataTable from '../../Shared/DataTable.vue'
import { SquarePen, Trash2 } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from '../../V3/Experience/components/Search.vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'App',
    props: ['hasEdit', 'baseUrl'],
    components: {
        DataTable,
        Header,
        SquarePen,
        Trash2,
        Search,
    },
    data() {
        return {
            items: [],
            loading: false,
            currentPage: 1,
            perPage: 10,
            totalEntries: 0,
            searchQuery: '',
            sortKey: '',
            sortOrder: 'asc',
            searchTimeout: null,

            // API Configuration
            apiHeaders: {
                Authorization: 'Bearer your-token-here',
                'X-Requested-With': 'XMLHttpRequest',
            },

            // Users Table Configuration
            userColumns: [
                {
                    key: 'system_title',
                    label: 'system_title',
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'title_notif',
                    label: 'title_notif',
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'desc_notif',
                    label: 'desc_notif',
                    sortable: false,
                    type: 'text',
                },
                {
                    key: 'send_time',
                    label: 'send_time',
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'end_date',
                    label: 'end_date',
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'send',
                    label: 'send',
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'updated_at',
                    label: 'updated_at',
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'actions',
                    label: this.$t('datatable_model_template_edit.action'),
                    sortable: false,
                    type: 'actions',
                },
            ],
            activeTab: 'current',
            datatableKey: 0,
        }
    },
    computed: {
        usersApiUrl() {
            return this.activeTab === 'current'
                ? '/back-office/communications/notifications/list?history=false'
                : '/back-office/communications/notifications/list?history=true'
        },
        totalPages() {
            return Math.ceil(this.totalEntries / this.perPage)
        },
    },
    mounted() {
        this.fetchData()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async fetchData() {
            this.loading = true
            try {
                const params = {
                    page: this.currentPage,
                    per_page: this.perPage,
                    search: this.searchQuery,
                    sort_by: this.sortKey,
                    sort_order: this.sortOrder,
                }
                const response = await this.makeApiRequest(params)
                this.handleApiResponse(response)
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.onError(error)
            } finally {
                this.loading = false
            }
        },
        async makeApiRequest(params) {
            const queryString = new URLSearchParams(params).toString()
            const url = `${this.usersApiUrl}&${queryString}`

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.apiHeaders,
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            return await response.json()
        },
        handleApiResponse(response) {
            let _response = response.data
            if (_response.data && Array.isArray(_response.data)) {
                this.items = _response.data
                this.totalEntries = _response.total || _response.data.length
                this.onDataLoaded({ data: this.items, total: this.totalEntries })
            } else if (Array.isArray(_response)) {
                this.items = _response
                this.totalEntries = _response.length
                this.onDataLoaded({ data: this.items, total: this.totalEntries })
            } else {
                this.items = []
                this.totalEntries = 0
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        onSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1
                this.fetchData()
            }, 500)
        },
        onSort(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortKey = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchData()
        },
        onDataLoaded(response) {
            console.log('Data loaded:', response)
        },
        onError(error) {
            console.error('Datatable error:', error)
            alert('Error loading data: ' + error.message)
        },
        formatDate(date) {
            if (!date) return ''
            const d = new Date(date)
            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
        },
        handlePage() {
            window.location.href = `${window.location.origin}/back-office/communications/notifications/create`
        },
        changeTab(tab) {
            this.activeTab = tab
            this.datatableKey++
            this.currentPage = 1
            this.fetchData()
        },
    },
}
</script>
